import { useEffect, useRef, useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { completePasskeySignIn, startPasskeySignIn } from "@/lib/mfa-api";
import { getPasskey, passkeyAutofillSupported, passkeySupportedInBrowser } from "@/lib/webauthn";

interface PasskeySignInProps {
	disabled: boolean;
	onSignedIn: () => void;
	/** null で表示中のエラーを消す。 */
	onError: (message: string | null) => void;
	onAvailabilityChange: (availability: PasskeyAvailability) => void;
}

/** checking はブラウザに確認中。usable の間だけボタンを出す。 */
export type PasskeyAvailability = "checking" | "usable" | "unusable";

/** サーバーのチャレンジの寿命 (5 分) が切れる前に、自動入力の待ち受けを新しいチャレンジでかけ直す。 */
const AUTOFILL_REFRESH_MS = 4 * 60 * 1000;

/**
 * メールアドレスもパスワードも入れずに、パスキーだけでサインインするボタン。
 * ブラウザが対応していれば、メールアドレス欄の自動入力の候補にもパスキーを出す。
 * ブラウザが対応していればすぐ主色で出し、この環境 (IP アドレスや http の接続先) で使えないとサーバーが答えたら消す。
 */
export function PasskeySignIn({
	disabled,
	onSignedIn,
	onError,
	onAvailabilityChange,
}: PasskeySignInProps) {
	const [availability, setAvailability] = useState<PasskeyAvailability>("checking");
	const [signingIn, setSigningIn] = useState(false);
	/** 増やすと、自動入力の待ち受けを新しいチャレンジでかけ直す。 */
	const [autofillRound, setAutofillRound] = useState(0);
	const stopAutofill = useRef<() => void>(() => undefined);

	useEffect(() => onAvailabilityChange(availability), [availability, onAvailabilityChange]);

	useEffect(() => {
		const controller = new AbortController();
		let refresh: ReturnType<typeof setTimeout> | undefined;
		const stop = () => {
			clearTimeout(refresh);
			controller.abort();
		};
		stopAutofill.current = stop;

		/** 自動入力の候補からパスキーが選ばれるのを待つ。使えない・中断されたら null。 */
		async function waitForAutofill() {
			const supported = await passkeySupportedInBrowser();
			if (controller.signal.aborted) return null;
			if (!supported) {
				setAvailability("unusable");
				return null;
			}
			// サーバーの応答を待たずにボタンを出す。
			setAvailability("usable");
			let ceremony;
			try {
				ceremony = await startPasskeySignIn();
			} catch (err) {
				// 押したときに改めて準備し、そこで失敗を見せる。パスワードでのサインインは妨げない。
				console.error("パスキーのサインインを準備できませんでした", err);
				return null;
			}
			if (controller.signal.aborted) return null;
			if (!ceremony) {
				setAvailability("unusable");
				return null;
			}
			if (!(await passkeyAutofillSupported())) return null;

			refresh = setTimeout(() => {
				stop();
				setAutofillRound((n) => n + 1);
			}, AUTOFILL_REFRESH_MS);
			try {
				const credential = await getPasskey(ceremony.options, {
					mediation: "conditional",
					signal: controller.signal,
				});
				return credential && { state: ceremony.state, credential };
			} finally {
				clearTimeout(refresh);
			}
		}

		/** 利用者が候補から選んだ後なので、失敗は画面に出し、また候補から選べるようにする。 */
		async function signInWithChosen(state: string, credential: unknown) {
			setSigningIn(true);
			onError(null);
			try {
				await completePasskeySignIn(state, credential);
				onSignedIn();
			} catch (err) {
				console.error("パスキーでサインインできませんでした", err);
				onError(signInErrorMessage(err));
				setAutofillRound((n) => n + 1);
			} finally {
				setSigningIn(false);
			}
		}

		waitForAutofill().then(
			(chosen) => {
				if (chosen) void signInWithChosen(chosen.state, chosen.credential);
			},
			(err: unknown) => {
				// 利用者が何もしていない準備の段階の失敗。ボタンとパスワードでのサインインは使えるため、表示はしない。
				console.error("パスキーの自動入力を準備できませんでした", err);
			},
		);
		return stop;
	}, [autofillRound, onError, onSignedIn]);

	async function signIn() {
		// 自動入力の待ち受けが残っていると、ダイアログを出す要求が受け付けられない。
		stopAutofill.current();
		setSigningIn(true);
		onError(null);
		let rearmAutofill = true;
		try {
			const ceremony = await startPasskeySignIn();
			if (!ceremony) {
				rearmAutofill = false;
				setAvailability("unusable");
				onError(
					"この環境ではパスキーを使えません。メールアドレスとパスワードでサインインしてください。",
				);
				return;
			}
			const credential = await getPasskey(ceremony.options);
			if (!credential) return;
			await completePasskeySignIn(ceremony.state, credential);
			rearmAutofill = false;
			onSignedIn();
		} catch (err) {
			console.error("パスキーでサインインできませんでした", err);
			onError(signInErrorMessage(err));
		} finally {
			setSigningIn(false);
			// 取り消し・失敗のあとも、自動入力の候補からまた選べるようにする。
			if (rearmAutofill) setAutofillRound((n) => n + 1);
		}
	}

	if (availability !== "usable") return null;

	return (
		<>
			<ConfirmButton
				label="パスキーでサインイン"
				icon="key"
				loading={signingIn}
				disabled={disabled}
				onClick={() => void signIn()}
				data-testid="passkey-sign-in"
			/>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 8,
					fontSize: "var(--text-caption)",
					color: "var(--fg-3)",
				}}
			>
				<hr style={{ flex: 1, border: "none", borderTop: "1px solid var(--line-1)", margin: 0 }} />
				または
				<hr style={{ flex: 1, border: "none", borderTop: "1px solid var(--line-1)", margin: 0 }} />
			</div>
		</>
	);
}

function signInErrorMessage(err: unknown): string {
	return err instanceof Error ? err.message : "パスキーでサインインできませんでした。";
}
